import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Switch,
  TextField,
  Theme,
  Typography
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import SaveIcon from '@material-ui/icons/SaveOutlined';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useState} from 'react';
import {useT} from 'react-polyglot-hooks';
import {Link as RouterLink} from 'react-router-dom';
import useRouter from 'use-react-router';
import {Can} from '../../../../components/Can';
import ConfirmDelete from '../../../../components/ConfirmDelete';
import InfoPopover from '../../../../components/InfoPopover';
import {LoadingOverlay} from '../../../../components/LoadingOverlay';
import {Communication, CommunicationTypes, Receivers} from '../../../../graphql/comms.datatypes';
import logCodes from '../../../../graphql/logCodes.api';
import anyLoading from '../../../../utils/anyLoading';
import createCommunication from './createCommunication.api';
import deleteCommunication from './deleteCommunication.api';
import updateCommunication from './updateCommunication.api';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(3),
  },
  card: {
    padding: 0,
  },
  input: {
    marginBottom: theme.spacing(3),
  },
  divider: {
  },
  titleCell: {
    width: '40%',
  },
  dataCell: {
    width: '60%',
  },
  actions: {
    justifyContent: 'flex-end'
  },
  redText: {
    color: theme.palette.error.dark,
  },
  greenText: {
    color: colors.green[300],
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    }
  },
}));

const CommunicationEdit = (props: { communication: Communication, }) => {
  const {communication} = props;
  const classes = useStyles();
  const {history} = useRouter();
  const [sendCreateCommunication, createCommunicationData] = createCommunication();
  const [sendUpdateCommunication, updateCommunicationData] = updateCommunication();
  const [sendDelete, deleteData] = deleteCommunication();
  const logCodesData = logCodes();
  const loadingData = anyLoading(createCommunicationData, updateCommunicationData, deleteData, logCodesData);
  const [formState, setFormState] = useState({...communication});
  const [openDelete, setOpenDelete] = useState(false);
  const newCommunication = communication.id === -1;
  const {enqueueSnackbar} = useSnackbar();
  const t = useT();

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    event.persist();
    setFormState((oldState: any) => ({
      ...oldState,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? (event.target as HTMLInputElement).checked
          : event.target.value
    }));
  };
  const handleTriggerChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    event.persist();
    const parts = event.target.name.split('_');
    const index: number = Number(parts.pop());
    const key = parts.join('_');

    setFormState((oldState: any) => ({
      ...oldState,
      triggerValues: [
        ...oldState.triggerValues.filter((trigger: any, i: number) => (i !== index)),
        {
          ...oldState.triggerValues[index],
          [key]: event.target.value
        }
      ]
    }));
  };

  const onSaveClick = () => {

    //Sanitise formstate for graphql
    const sanitisedData = {
      ...formState,
      triggerValues: undefined,
    };
    delete sanitisedData.__typename;
    delete sanitisedData.triggerValues;
    sanitisedData.receiver = Number(sanitisedData.receiver);

    if (newCommunication) {

      delete sanitisedData.id;

      sendCreateCommunication({
        variables: {data: sanitisedData},
      })
          .then(() => {
            enqueueSnackbar('Communication created', {variant: 'success'});
            history.push('/comms');
          })
        .catch(() => {
          enqueueSnackbar('Communication creation failed', {variant: 'error'});
        });
    } else {

      sendUpdateCommunication({
        variables: {data: sanitisedData},
        optimisticResponse: {
          __typename: 'Mutation',
          updateCommunication: {
            ...sanitisedData,
            __typename: 'Communication',
            triggerValues: formState.triggerValues,
          },
        }
      })
        .then(() => {
          enqueueSnackbar('Communication updated', {variant: 'success'});
        })
        .catch(() => {
          enqueueSnackbar('Communication update failed', {variant: 'error'});
        });
    }
  };

  const handleAddTrigger = () => {
    const sanitisedData = {
      id: communication.id,
      triggerValues: {
        create: [{
          trigger_key: 'log.payload.key',
          trigger_value: 'your value',
        }],
      },
    };

    sendUpdateCommunication({
      variables: {data: sanitisedData},
    })
      .then((response: any) => {
        const oldIds = formState.triggerValues!.map(trigger => trigger.id);
        const newTrigger = response.data.updateCommunication.triggerValues.filter((trigger: any) => oldIds.indexOf(trigger.id) === -1).pop();

        setFormState((oldState: any) => ({
          ...oldState,
          triggerValues: [
            ...oldState.triggerValues,
            newTrigger,
          ]
        }));

        enqueueSnackbar('Trigger added', {variant: 'success'});
      })
      .catch(() => {
        enqueueSnackbar('Trigger add failed', {variant: 'error'});
      });
  };

  const handleDeleteTrigger = (id: number) => {
    const sanitisedData = {
      id: communication.id,
      triggerValues: {
        delete: [id],
      },
    };

    sendUpdateCommunication({
      variables: {data: sanitisedData},
    })
      .then(() => {
        setFormState((oldState: any) => ({
          ...oldState,
          triggerValues: [
            ...oldState.triggerValues.filter((trigger: any) => trigger.id !== id),
          ]
        }));
        enqueueSnackbar('Trigger deleted', {variant: 'success'});
      })
      .catch(() => {
        enqueueSnackbar('Trigger delete failed', {variant: 'error'});
      });
  };

  const handleUpdateTrigger = (id: number) => {
    if(formState.triggerValues) {
      const trigger :any = {...formState.triggerValues.filter(item => item.id === id).pop()};
      delete trigger.__typename;

      if(trigger) {
        const sanitisedData = {
          id: communication.id,
          triggerValues: {
            update: [trigger],
          },
        };

        sendUpdateCommunication({
          variables: {data: sanitisedData},
        })
          .then(() => {
            enqueueSnackbar('Trigger updated', {variant: 'success'});
          })
          .catch(() => {
            enqueueSnackbar('Trigger update failed', {variant: 'error'});
          });
      }
    }
  };

  const onDeleteClick = () => {
    sendDelete({variables: {id: communication.id}})
      .then(() => {
        enqueueSnackbar('Communication deleted', {variant: 'success'});
        history.push('/comms');
      })
      .catch(() => {
        enqueueSnackbar('Delete failed', {variant: 'error'});
      });
  };

  const handleDeleteOpen = () => {
    setOpenDelete(true);
  };

  const handleDeleteClose = () => {
    setOpenDelete(false);
  };

  return (
    <form>
      <LoadingOverlay loading={loadingData.loading}>
        <Grid className={classes.root} container spacing={3}>

          <Grid item lg={6} md={6} xl={4} xs={12}>
            <Card className={classes.card}>
              <CardHeader title="What are you sending?"/>
              <CardContent>
                <TextField
                  className={classes.input}
                  fullWidth
                  label="Title"
                  name="title"
                  onChange={handleFieldChange}
                  value={String(formState.title)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover message="The title of our message. This is not available in an SMS message." />
                    </InputAdornment>,
                  }}
                />

                <TextField
                  className={classes.input}
                  fullWidth
                  label="Body"
                  name="body"
                  onChange={handleFieldChange}
                  value={String(formState.body)}
                  variant="outlined"
                  multiline
                  rows={3}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover >
                        The body of our message.<br/>
                        This can contain varaibles that will be replaced by the payload data from the log that triggers the message.<br/>
                        {'{'}variable_name{'}'} will be replaced with a value from the payload called variable_name<br/>
                      </InfoPopover>
                    </InputAdornment>,
                  }}
                />

                <TextField
                  className={classes.input}
                  fullWidth
                  label="Ident"
                  name="ident"
                  onChange={handleFieldChange}
                  value={String(formState.ident)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover message="This is a unique identifier for the message in the system. It can be anything and will never be seen by the customer. " />
                    </InputAdornment>,
                  }}
                />
              </CardContent>

              <CardActions className={classes.actions}>
                {!newCommunication && (
                  <Can permission="communication.delete">
                    <Button
                      className={classes.deleteButton}
                      variant="contained"
                      size="small"
                      onClick={handleDeleteOpen}
                    >
                      Delete
                    </Button>
                  </Can>
                )}
                <Button
                  variant="contained"
                  component={RouterLink}
                  size="small"
                  to={`/comms`}
                >
                  Cancel
                </Button>
                <Button className={classes.saveButton} onClick={onSaveClick} variant="contained">Save</Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item lg={6} md={6} xl={4} xs={12}>
            <Card className={classes.card}>
              <CardHeader title="How are you sending it?"/>
              <CardContent>
                <TextField
                  className={classes.input}
                  fullWidth
                  label="Select Type"
                  name="communication_type"
                  onChange={handleFieldChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{native: true}}
                  value={String(formState.communication_type)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover message="This is the type of communication we are sending." />
                    </InputAdornment>,
                  }}
                >
                  <option key="-1" value="NONE">please select...</option>
                  {CommunicationTypes.map((type, i) => (
                    <option
                      key={type.name}
                      value={i}
                    >
                      {type.name}
                    </option>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  className={classes.input}
                  label="Select Receiver"
                  name="receiver"
                  onChange={handleFieldChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{native: true}}
                  value={String(formState.receiver)}
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover>
                        This is the person or group we are sending the message to.<br/><br/>
                        In the example of and admin user editing a customer details: <br/>
                        Reporter is the admin. <br/>
                        Target is the customer <br/>
                        admins is all admins <br/>
                        super admins is all super admins.
                      </InfoPopover>
                    </InputAdornment>,
                  }}
                >
                  <option key="-1" value="NONE">please select...</option>
                  {Receivers.map((receiver, i) => (
                    <option
                      key={receiver.name}
                      value={i}
                    >
                      {receiver.name}
                    </option>
                  ))}
                </TextField>
              </CardContent>
            </Card>
          </Grid>

          <Grid item lg={6} md={6} xl={4} xs={12}>
            <Card className={classes.card}>
              <CardHeader
                title="What triggers it sending?"
                action={(!newCommunication && (<Button className={classes.saveButton} onClick={handleAddTrigger} variant="contained">New Trigger Value</Button>))}
              />
              <CardContent>
                <FormGroup row className={classes.input}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.active}
                        color="primary"
                        name="active"
                        onChange={handleFieldChange}
                        value={formState.active}
                      />
                    }
                    label="Is this message active?"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.mandatory}
                        color="primary"
                        name="mandatory"
                        onChange={handleFieldChange}
                        value={formState.mandatory}
                      />
                    }
                    label="Is this message mandatory?"
                  />

                </FormGroup>

                <TextField
                  className={classes.input}
                  fullWidth
                  label="Select Log Code"
                  name="log_code"
                  onChange={handleFieldChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{native: true}}
                  value={formState.log_code}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">
                      <InfoPopover>
                        This is the system log that will cause the message to be sent. <br/>
                        Whenever this log is captured in the system the message will send. <br/>
                        Each log has a payload object with values you can access in the Trigger Values. <br/>
                        You can add Trigger Values to further refine if the message will send by testing what is in the payload.
                      </InfoPopover>
                    </InputAdornment>,
                  }}
                >
                  <option key="-1" value="NONE">please select...</option>
                  {logCodesData.data && logCodesData.data.logCodes && logCodesData.data.logCodes.map((logCode) => (
                    <option
                      key={logCode}
                      value={logCode}
                    >
                      {logCode}: {t(logCode)}
                    </option>
                  ))}
                </TextField>

                <Typography>
                  If you only want the Log to trigger this message to send if some of its payload values match you can add Trigger Values.
                </Typography>
                <br/>
                <Typography>
                  Each Log in the system has a JSON formatted payload object with values relevant to the Log entry.
                  Trigger Values are used to check the value of varibles present in the Log payload.
                  You can access values in the payload using dot notation to select a key.
                  You can access by using a trigger key to read a value, and a trigger value to test for equality.
                  To see a payload for a Log, search in the Logs view for the Log Code and look at how it is structured.
                </Typography>
                <br/>
                {formState.log_code !== '' && (<Link color="primary" variant="h6" component={RouterLink} to={`/logs/${formState.log_code}`}>See {formState.log_code} Logs</Link>)}
                <br/>
                <br/>
                <Typography>
                  i.e. a payload structured like {JSON.stringify({id: 10, note: 'test', child: {name: 'Jim'}})}
                </Typography>
                <br/>
                <Typography>
                  key: id value: 10 <br/>
                  key: child.name value: Jim<br/>
                  <br/>
                </Typography>

                {formState!.triggerValues!.map((triggerValue, i) => {
                  return (
                    <FormGroup key={triggerValue.id} row className={classes.input}>
                      <TextField
                        style={{width: '37%'}}
                        label="Trigger Key"
                        name={'trigger_key_' + i}
                        onChange={handleTriggerChange}
                        value={formState.triggerValues![i].trigger_key}
                        variant="outlined"
                      />
                      <TextField
                        style={{width: '37%'}}
                        label="Trigger Value"
                        name={'trigger_value_' + i}
                        onChange={handleTriggerChange}
                        value={formState.triggerValues![i].trigger_value}
                        variant="outlined"
                      />
                      <IconButton className={classes.greenText} onClick={() => handleUpdateTrigger(triggerValue.id)}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton className={classes.redText} onClick={() => handleDeleteTrigger(triggerValue.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </FormGroup>
                  );
                })}

              </CardContent>
            </Card>
          </Grid>

        </Grid>

        <ConfirmDelete onClose={handleDeleteClose} onDeleteClick={onDeleteClick} open={openDelete}/>
      </LoadingOverlay>
    </form>
  );
};

export default CommunicationEdit;
